/*
  Old furniture removal offer.

  Product page: the tab renders above Add to cart (from buy-buttons.liquid, so it
  works on every product template) and then moves itself into the product's tab
  list, after "Delivery or Storage" or after the last tab. When the box is ticked,
  "Add to cart" sends the product and the removal line in one request.
  Cart drawer / cart page: "Add" and "Remove" change the cart and re-render the
  relevant sections without a page reload.

  Rows (data-ru-row) carry data-sku. Controls inside a row:
    [data-ru-tick]    the row's "Yes, take my old ... away" checkbox (product
                      page). The row gets an is-ticked class while it is
                      checked, which the CSS uses to turn the picture card
                      green - a class rather than :has() because browsers do
                      not reliably restyle an ancestor when a descendant's
                      :checked state flips.
    [data-ru-choice]  radio cards (seating type) - the checked value is the
                      SKU; picking one on the product page also ticks the row.
    [data-ru-qty]     a theme <quantity-input> count (dining chairs) - the line
                      quantity, priced per unit.

  ALREADY IN THE BASKET. Outlet stock is mostly one-of-one, so a customer who
  comes back to a product page and presses Add to cart again gets Shopify's
  "already in your cart" refusal. window.cartAlreadyHas(response) recognises
  that reply; assets/product-form.js and the submit handler below both use
  it to open the cart drawer instead of showing an error. When removal was
  ticked, any removal line not already in the basket is added on its own
  first (removal is untracked, so it must not be added twice).

  NO LINE ITEM PROPERTIES. Removal lines used to carry "Old item" and
  "Collected with" properties, which showed as small grey text under the line
  in the cart drawer. They made an already busy drawer harder to read, and the
  variant title already says what is being collected. If they are ever wanted
  back, add a `properties` object to the object returned by lineItemFor.
*/
window.cartAlreadyHas = function (response) {
  if (!response || response.status !== 422) return false;
  const text = [response.description, response.message, response.errors].filter(Boolean).join(' ');
  return /in your cart/i.test(text);
};

if (!customElements.get('removal-upsell')) {
  class RemovalUpsell extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.context = this.dataset.context;
      this.sectionId = this.dataset.sectionId;

      if (this.context === 'product' && !this.dataset.placed && this.placeInTabList()) return;

      try {
        this.variants = JSON.parse(this.querySelector('[data-removal-variants]').textContent);
      } catch (e) {
        this.variants = {};
      }
      this.addEventListener('click', this.onClick);
      /* Capture so the drawer's cart-items change handler never sees our inputs. */
      this.addEventListener('change', this.onChange, true);
      this.addEventListener('input', this.onChange, true);
      this.querySelectorAll('[data-ru-row]').forEach((row) => {
        this.syncRow(row);
        this.syncTick(row);
      });

      if (this.context === 'cart' && window.subscribe && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'removal-upsell') return;
          this.refreshSection();
        });
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /* Move into the product's tab list. Returns true when the element was moved
       (connectedCallback runs again at the new position). */
    placeInTabList() {
      const container =
        this.closest('.product__info-container') || this.closest('.product__info-wrapper') || this.closest('.shopify-section');
      if (!container) return false;
      const tabs = Array.from(container.querySelectorAll('.product__accordion')).filter((t) => !this.contains(t));
      if (!tabs.length) return false;
      const preferred = tabs.find((t) => /delivery or storage/i.test((t.querySelector('.accordion__title') || {}).textContent || ''));
      const target = preferred || tabs[tabs.length - 1];
      this.dataset.placed = 'true';
      target.after(this);
      return true;
    }

    /* ---------- helpers ---------- */

    money(pence) {
      return '£' + (pence / 100).toFixed(2);
    }

    clampInput(input) {
      const min = Number(input.min) || 1;
      const max = Number(input.max) || 99;
      let n = parseInt(input.value, 10);
      if (isNaN(n)) n = min;
      n = Math.min(max, Math.max(min, n));
      if (String(n) !== input.value) input.value = String(n);
      return n;
    }

    rowState(row) {
      const kind = row.dataset.kind;
      let sku = row.dataset.sku;
      let quantity = 1;

      const choice = row.querySelector('[data-ru-choice]:checked');
      if (choice) sku = choice.value;

      const qty = row.querySelector('[data-ru-qty]');
      if (qty) quantity = this.clampInput(qty);

      const variant = this.variants[sku];
      return { kind, sku, quantity, variant };
    }

    syncRow(row) {
      const state = this.rowState(row);
      row.dataset.sku = state.sku;
      if (!state.variant) return;
      const price = row.querySelector('[data-ru-price]');
      if (price) price.textContent = this.money(state.variant.price * state.quantity);
    }

    /* Mirror the row's checkbox onto the row as a class for the CSS. */
    syncTick(row) {
      const tick = row.querySelector('[data-ru-tick]');
      row.classList.toggle('is-ticked', !!(tick && tick.checked));
    }

    lineItemFor(row) {
      const state = this.rowState(row);
      if (!state.variant) return null;
      return {
        id: state.variant.id,
        quantity: state.quantity,
      };
    }

    /* Removal lines ticked on the product page, ready to be sent with the product. */
    tickedLineItems() {
      const items = [];
      this.querySelectorAll('[data-ru-row]').forEach((row) => {
        const tick = row.querySelector('[data-ru-tick]');
        if (tick && tick.checked) {
          const item = this.lineItemFor(row);
          if (item) items.push(item);
        }
      });
      return items;
    }

    showError(message) {
      const el = this.querySelector('.removal-upsell__error');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* ---------- events ---------- */

    onChange(event) {
      const row = event.target.closest('[data-ru-row]');
      if (!row) return;
      /* Keep the theme's cart-items handler out of it: these inputs are not cart lines. */
      event.stopPropagation();
      if (event.target.matches('[data-ru-choice], [data-ru-qty]')) {
        this.syncRow(row);
      }
      /* Choosing a seating picture on the product page is a clear yes: tick the
         row. (The default picture is only preselected; the box starts unticked.) */
      if (event.type === 'change' && event.target.matches('[data-ru-choice]') && this.context === 'product') {
        const tick = row.querySelector('[data-ru-tick]');
        if (tick) tick.checked = true;
      }
      this.syncTick(row);
    }

    onClick(event) {
      const add = event.target.closest('[data-ru-add]');
      const remove = event.target.closest('[data-ru-remove]');

      if (add) {
        const row = add.closest('[data-ru-row]');
        const item = this.lineItemFor(row);
        if (!item) return;
        add.disabled = true;
        this.changeCart(window.routes.cart_add_url, { items: [item] }).finally(() => {
          add.disabled = false;
        });
        return;
      }

      if (remove) {
        remove.disabled = true;
        this.changeCart(window.routes.cart_change_url, { id: remove.dataset.ruRemove, quantity: 0 }).finally(() => {
          remove.disabled = false;
        });
      }
    }

    /* ---------- cart changes from the drawer or cart page ---------- */

    sectionsToRender() {
      if (this.context === 'drawer') {
        return [
          { id: 'cart-drawer', selector: '#CartDrawer' },
          { id: 'cart-icon-bubble' },
        ];
      }
      const list = [];
      const mainItems = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (mainItems) list.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
      if (footer) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
      list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
      if (this.sectionId) list.push({ id: 'shopify-section-' + this.sectionId, section: this.sectionId, selector: '.shopify-section' });
      return list;
    }

    changeCart(url, body) {
      this.showError('');
      const sections = this.sectionsToRender();
      const payload = Object.assign({}, body, {
        sections: sections.map((s) => s.section || s.id),
        sections_url: window.location.pathname,
      });
      const config = window.fetchConfig ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
      config.body = JSON.stringify(payload);

      return fetch(url, config)
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message || 'Sorry, that could not be added. Please try again.');
            return;
          }
          if (this.context === 'drawer') {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && response.sections) {
              drawer.renderContents(response);
            }
          } else {
            sections.forEach((section) => {
              const html = response.sections && response.sections[section.section];
              if (!html) return;
              const target = document.getElementById(section.id);
              if (!target) return;
              const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
              const slot = target.querySelector(section.selector) || target;
              if (fresh) slot.innerHTML = fresh.innerHTML;
            });
            const footer = document.getElementById('main-cart-footer');
            if (footer) footer.classList.toggle('is-empty', response.item_count === 0);
          }
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'removal-upsell', cartData: response });
          }
        })
        .catch(() => {
          this.showError('Sorry, something went wrong. Please try again.');
        });
    }

    refreshSection() {
      if (!this.sectionId) return;
      const target = document.getElementById('shopify-section-' + this.sectionId);
      if (!target) return;
      fetch(`${window.routes.cart_url}?section_id=${this.sectionId}`)
        .then((r) => r.text())
        .then((html) => {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (fresh) target.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    }
  }

  customElements.define('removal-upsell', RemovalUpsell);

  /*
    Product page: intercept the add-to-cart submit in the capture phase, before the
    theme's own handler, and send both lines together when removal is ticked.
  */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.type !== 'add-to-cart-form') return;
      /* form.id is shadowed by the hidden input named "id", so read the attribute. */
      const formId = form.getAttribute('id') || '';
      if (formId.indexOf('product-form-') !== 0) return;

      const upsell = document.querySelector('removal-upsell[data-context="product"]');
      if (!upsell) return;
      const extras = upsell.tickedLineItems();
      if (!extras.length) return;

      const productForm = form.closest('product-form');
      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const formData = new FormData(form);
      const mainId = Number(formData.get('id'));
      if (!mainId) return;
      const main = { id: mainId, quantity: Number(formData.get('quantity')) || 1, properties: {} };
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) main.properties[match[1]] = value;
      }
      if (formData.get('selling_plan')) main.selling_plan = formData.get('selling_plan');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const drawer = document.querySelector('cart-drawer');
      const sections = cart ? cart.getSectionsToRender().map((s) => s.id) : null;
      const withSections = (items) => {
        const payload = { items: items };
        if (sections) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }
        return payload;
      };
      if (cart && cart.setActiveElement) cart.setActiveElement(document.activeElement);

      if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage();
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        const spinner = productForm && productForm.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      const post = (payload) => {
        const config = window.fetchConfig ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
        config.body = JSON.stringify(payload);
        return fetch(window.routes.cart_add_url, config).then((r) => r.json());
      };

      const finish = (response) => {
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: mainId, cartData: response });
        }
        cart.renderContents(response);
      };

      post(withSections([main].concat(extras)))
        .then((response) => {
          if (!response.status) return finish(response);

          /* The product itself is already in the basket. Add only the removal
             line(s) the basket does not have yet, then show the basket; if
             there is nothing left to add, just open the drawer. */
          if (drawer && window.cartAlreadyHas(response)) {
            return fetch(window.routes.cart_url + '.js')
              .then((r) => r.json())
              .then((current) => {
                const have = new Set((current.items || []).map((i) => i.variant_id));
                const missing = extras.filter((e) => !have.has(e.id));
                if (!missing.length) {
                  drawer.open(submitButton);
                  return;
                }
                return post(withSections(missing)).then((second) => {
                  if (!second.status) return finish(second);
                  drawer.open(submitButton);
                });
              });
          }

          if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage(response.description);
          else upsell.showError(response.description);
        })
        .catch(() => {
          upsell.showError('Sorry, something went wrong. Please try again.');
        })
        .finally(() => {
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
            const spinner = productForm && productForm.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          }
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        });
    },
    true
  );
}
